/**
 * useNetobsTick — React hook subscribing to the server's `netobs:tick`
 * broadcast (TickHealthService). Powers NetobsTickBadge (NO-2).
 *
 * Backed by useColyseusMessages so we get the bind/rebind-on-reconnect
 * semantics for free + match the codebase's hook adoption pattern.
 *
 * Server emits roughly every 0.5s (30 ticks at 60Hz). The hook just
 * stores the latest snapshot — consumers re-render when it changes.
 */

import { useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { isPopoutMode } from '../../systems/netobs/popout/popoutMode';
import { popoutShim } from '../../systems/netobs/popout/popoutShimState';

export interface NetobsTickSubsystemStats {
  meanMs: number;
  maxMs: number;
  count: number;
}

export interface NetobsTickSnapshot {
  meanMs: number;
  maxMs: number;
  sampleCount: number;
  budgetMs: number;
  /** When the server emitted this aggregate (ms epoch). */
  timestamp: number;
  /** ms-since-receive on this client. Updated on consumer poll if needed. */
  receivedAt: number;
  /** Per-subsystem rollup (E1) — present when the server uses
   *  `tickHealth.subsystem(name, fn)` to time each subsystem. Lets
   *  NetobsTickBadge render which subsystem ate the budget. Optional
   *  for back-compat with pre-E1 server builds. */
  subsystems?: Record<string, NetobsTickSubsystemStats>;
}

const EMPTY: NetobsTickSnapshot | null = null;

function parseSubsystems(raw: unknown): Record<string, NetobsTickSubsystemStats> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, NetobsTickSubsystemStats> = {};
  for (const [name, val] of Object.entries(raw as Record<string, unknown>)) {
    if (!val || typeof val !== 'object') continue;
    const v = val as { meanMs?: unknown; maxMs?: unknown; count?: unknown };
    out[name] = {
      meanMs: Number(v.meanMs) || 0,
      maxMs: Number(v.maxMs) || 0,
      count: Number(v.count) || 0,
    };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function useNetobsTick(): NetobsTickSnapshot | null {
  const [snap, setSnap] = useState<NetobsTickSnapshot | null>(EMPTY);
  const popout = isPopoutMode();

  // Live mode: subscribe to the server's netobs:tick broadcast.
  // (useColyseusMessages no-ops if there's no room — popout has none.)
  useColyseusMessages({
    'netobs:tick': (msg: any) => {
      if (popout) return;  // popout reads from shim instead
      if (!msg || typeof msg !== 'object') return;
      setSnap({
        meanMs: Number(msg.meanMs) || 0,
        maxMs: Number(msg.maxMs) || 0,
        sampleCount: Number(msg.sampleCount) || 0,
        budgetMs: Number(msg.budgetMs) || 16.67,
        timestamp: Number(msg.timestamp) || Date.now(),
        receivedAt: Date.now(),
        subsystems: parseSubsystems((msg as { subsystems?: unknown }).subsystems),
      });
    },
  });

  // Popout mode: poll the BroadcastChannel-fed shim every 250ms.
  useEffect(() => {
    if (!popout) return;
    const tick = () => {
      const t = popoutShim.getTick();
      if (t) setSnap(t);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [popout]);

  return snap;
}
